<template>
  <div class="dash">
    <div class="dash__header">
      <h1 class="dash__title">Dashboard</h1>
      <span class="dash__updated">Updated at {{ updatedAt }}</span>
    </div>

    <div class="dash__grid">
      <component
        v-for="card in cards"
        :key="card.key"
        :is="card.href ? 'a' : 'router-link'"
        v-bind="card.href ? { href: card.href, target: '_blank' } : { to: { name: card.route } }"
        class="dash__card"
        :class="`dash__card--${card.color}`"
      >
        <div class="dash__card-top">
          <span class="dash__card-label">{{ card.label }}</span>
          <i :class="['dash__card-icon', card.icon]"></i>
        </div>
        <div class="dash__card-value">
          <span v-if="loading">—</span>
          <span v-else>{{ fmt(stats[card.key]) }}</span>
        </div>
        <div class="dash__card-sub">{{ card.sub }}</div>
      </component>
    </div>

    <div class="dash__section-title">Reviews</div>
    <div class="dash__reviews">
      <router-link :to="{ name: 'Reviews' }" class="dash__card dash__card--green">
        <div class="dash__card-top">
          <span class="dash__card-label">Approved</span>
          <i class="dash__card-icon tim-icons icon-check-2"></i>
        </div>
        <div class="dash__card-value">
          <span v-if="loading">—</span>
          <span v-else>{{ fmt(stats.reviewsApproved) }}</span>
        </div>
        <div class="dash__card-sub">Published reviews</div>
      </router-link>

      <router-link :to="{ name: 'Reviews', query: { approved: 'false' } }" class="dash__card dash__card--orange">
        <div class="dash__card-top">
          <span class="dash__card-label">Pending approval</span>
          <i class="dash__card-icon tim-icons icon-time-alarm"></i>
        </div>
        <div class="dash__card-value">
          <span v-if="loading">—</span>
          <span v-else>{{ fmt(stats.reviewsPending) }}</span>
        </div>
        <div class="dash__card-sub">Not approved, rating &gt; 0</div>
      </router-link>
    </div>
  </div>
</template>

<script>
import APIClinics from '@/api/modules/clinics';
import APIDoctors from '@/api/modules/doctors';
import APICatalogs from '@/api/modules/cms-api-catalogs';
import APIOffers from '@/api/modules/offers';
import APIReviews from '@/api/modules/reviews';
import APIAgencies from '@/api/modules/agencies';

export default {
  name: 'Dashboard',
  data() {
    return {
      loading: true,
      updatedAt: '—',
      stats: {
        clinics: null,
        doctors: null,
        operations: null,
        diagnostics: null,
        offers: null,
        agencies: null,
        reviewsApproved: null,
        reviewsPending: null,
      },
      cards: [
        { key: 'clinics',     label: 'Clinics',     sub: 'Visible (not hidden)',  icon: 'tim-icons icon-bank',      route: 'Clinics',     color: 'blue'   },
        { key: 'doctors',     label: 'Doctors',     sub: 'Published profiles',    icon: 'tim-icons icon-single-02', route: 'Doctors',     color: 'blue'   },
        { key: 'operations',  label: 'Operations',  sub: 'Active entries',        icon: 'fa fa-user-md',            route: 'Operations',  color: 'teal'   },
        { key: 'diagnostics', label: 'Diagnostics', sub: 'Active entries',        icon: 'fa fa-stethoscope',        route: 'Diagnostics', color: 'teal'   },
        { key: 'offers',      label: 'Offers',      sub: 'Active offer-clinics',  icon: 'tim-icons icon-tag',       route: 'offers-list', color: 'purple' },
        { key: 'agencies',    label: 'Partners',    sub: 'Active agencies',       icon: 'tim-icons icon-world',     route: 'Agencies',    color: 'indigo' },
      ],
    };
  },
  created() {
    this.loadStats();
  },
  methods: {
    async loadStats() {
      this.loading = true;
      try {
        const [
          clinicsAll,
          clinicsHidden,
          doctors,
          operations,
          diagnostics,
          offers,
          agencies,
          reviewsApproved,
          reviewsPending,
        ] = await Promise.all([
          APIClinics.getClinics({ perPage: 1 }),
          APIClinics.getClinics({ perPage: 1, only_hide: true }),
          APIDoctors.getDoctors({ perPage: 1 }),
          APICatalogs.getOperations({ perPage: 1 }),
          APICatalogs.getDiagnostics({ perPage: 1 }),
          APIOffers.getOffersList({ perPage: 1, excludeTags: ['new'] }),
          APIAgencies.getStats(),
          APIReviews.getReviews({ perPage: 1, approved: 'true' }),
          APIReviews.getReviews({ perPage: 1, approved: 'false', score_from: 1 }),
        ]);

        // total_count = items total; total_page = pages (= items when perPage:1)
        const total = (r, key) => {
          const meta = r?.data?.meta ?? r?.data?.metadata;
          if (meta?.total_count != null) return meta.total_count;
          if (meta?.total       != null) return meta.total;
          if (meta?.total_page  != null) return meta.total_page;
          return r?.data?.[key]?.length ?? null;
        };

        const clinicsTotal = total(clinicsAll,    'clinics');
        const clinicsHide  = total(clinicsHidden, 'clinics');

        this.stats = {
          clinics:         clinicsTotal != null && clinicsHide != null ? clinicsTotal - clinicsHide : clinicsTotal,
          doctors:         total(doctors,         'doctors'),
          operations:      total(operations,      'operations'),
          diagnostics:     total(diagnostics,     'diagnostics'),
          offers:          total(offers,          'offers'),
          agencies:        agencies?.data?.total ?? null,
          reviewsApproved: total(reviewsApproved, 'reviews'),
          reviewsPending:  total(reviewsPending,  'reviews'),
        };

        const now = new Date();
        this.updatedAt = now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
      } catch (e) {
        console.error('Dashboard stats load failed', e);
      } finally {
        this.loading = false;
      }
    },
    fmt(n) {
      if (n == null) return '—';
      return Number(n).toLocaleString('en');
    },
  },
};
</script>

<style scoped lang="scss">
$blue:   #5c9ad0;
$teal:   #2a9d8f;
$purple: #7c6fcd;
$green:  #3cb371;
$orange: #e76f51;
$indigo: #4361ee;

.dash {
  padding: 8px 0;
}

.dash__header {
  display: flex;
  align-items: baseline;
  gap: 16px;
  margin-bottom: 28px;
}

.dash__title {
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  color: #1a1a2e;
}

.dash__updated {
  font-size: 12px;
  color: #999;
}

.dash__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 28px;
}

.dash__section-title {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #999;
  margin-bottom: 12px;
}

.dash__reviews {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
}

/* ── card ── */
.dash__card {
  display: block;
  background: #fff;
  border: 1px solid #e8ecf0;
  border-radius: 10px;
  padding: 20px 22px;
  text-decoration: none;
  transition: transform 0.15s, box-shadow 0.15s;
  cursor: pointer;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.10);
    text-decoration: none;
  }
}

.dash__card-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}

.dash__card-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: #888;
}

.dash__card-icon {
  font-size: 20px;
  color: #ccc;
}

.dash__card-value {
  font-size: 36px;
  font-weight: 700;
  line-height: 1;
  margin-bottom: 6px;
  color: #1a1a2e;
}

.dash__card-sub {
  font-size: 12px;
  color: #aaa;
}

/* color accents — left border + icon + value tint */
@each $name, $color in (blue: $blue, teal: $teal, purple: $purple, green: $green, orange: $orange, indigo: $indigo) {
  .dash__card--#{$name} {
    border-left: 4px solid $color;

    .dash__card-icon {
      color: $color;
    }

    .dash__card-value {
      color: $color;
    }
  }
}
</style>
